import {
	ArrayPick,
	Column,
	Conditional,
	Else,
	If,
	makeClassName,
	Row,
	Rule,
	stringToLocalDate,
	toLocalDateString,
	Tooltip,
	TooltipContainerProps,
	TooltipProps,
	TruncatedText,
} from "@hex-insights/core";
import {
	CountryNameFragment,
	HomeRoomSection,
	HomeRoomSectionNameFragment,
	LanguageNameFragment,
	PersonNameAndImageFragment,
	Student,
	StudentHomeRoomSectionEnrollment,
	useLazyTooltip,
	useStudentTooltipLazyQuery,
} from "../../../../Utilities";
import { ClassYearFormat, HomeRoomSectionFormat, PersonFormat } from "../../../Format";
import { ClassYearUtilities, StudentAcademicTermEnrollmentUtilities } from "../../../Utilities";
import { StudentEnrollmentStatusBadge } from "../Enrollment";
import { StudentIconNameDescription } from "../General";
import styles from "./styles.module.css";

export type TooltipStudent = Pick<Student, "id" | "graduationYear" | "yearsFromGraduation"> & {
	person: PersonNameAndImageFragment &
		Pick<Student["person"], "id" | "dateOfBirth" | "gender"> & {
			nationalities: CountryNameFragment[];
			languages: LanguageNameFragment[];
		};
	studentAcademicTermEnrollments: ArrayPick<
		Student["studentAcademicTermEnrollments"],
		"id" | "lateStartDate" | "earlyEndDate"
	>;
	studentHomeRoomSectionEnrollments: (Pick<StudentHomeRoomSectionEnrollment, "id"> & {
		homeRoomSection: Pick<HomeRoomSection, "id"> & HomeRoomSectionNameFragment;
	})[];
};

export type StudentTooltipProps = {
	isLoading?: boolean;
	student: TooltipStudent | null | undefined;
} & Omit<TooltipProps, "children">;

export function StudentTooltip({ isLoading = false, student, className, ...props }: StudentTooltipProps) {
	return (
		<Tooltip className={makeClassName(styles["tooltip"], "tooltip--big", className)} {...props}>
			<Tooltip.Body>
				<Conditional>
					<If condition={isLoading && !student}>Loading</If>
					<Else>{!!student && <StudentTooltipContent student={student} />}</Else>
				</Conditional>
			</Tooltip.Body>
		</Tooltip>
	);
}

type StudentTooltipContentProps = {
	student: TooltipStudent;
};

function StudentTooltipContent({ student }: StudentTooltipContentProps) {
	const { classYear } = ClassYearUtilities.useClassYear(student.yearsFromGraduation);
	const homeRoomSection = student.studentHomeRoomSectionEnrollments[0]?.homeRoomSection;
	const isEnrolled = student.studentAcademicTermEnrollments.some((e) =>
		StudentAcademicTermEnrollmentUtilities.isActive(e),
	);

	let description = "Student";
	if (!!homeRoomSection) {
		description = HomeRoomSectionFormat.name(homeRoomSection);
	} else if (!!classYear) {
		description = ClassYearFormat.name(classYear);
	}

	return (
		<Column justify="spaced-start">
			<StudentIconNameDescription
				student={student}
				noLink
				iconSize={40}
				description={
					<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem">
						<TruncatedText>{description}</TruncatedText>
						<Rule thickness="1px" color="var(--neutral-colors--tertiary)" style={{ height: "0.75rem" }} />
						<StudentEnrollmentStatusBadge isEnrolled={isEnrolled} />
					</Row>
				}
			/>

			<Column justify="spaced-start" verticalSpacing="0.125rem">
				<Row justify="spaced-start" align="center" verticalSpacing="0.25rem">
					<span className={styles["tooltip__label"]}>Age</span>
					<TruncatedText className={styles["tooltip__value"]}>
						{student.person.dateOfBirth ? PersonFormat.age(student.person.dateOfBirth) : "Unknown"}{" "}
						{student.person.dateOfBirth &&
							"(" + stringToLocalDate(student.person.dateOfBirth, "day").toLocaleDateString() + ")"}
					</TruncatedText>
				</Row>
				<Row justify="spaced-start" align="center" verticalSpacing="0.25rem">
					<span className={styles["tooltip__label"]}>Gender</span>
					<TruncatedText className={styles["tooltip__value"]}>
						{student.person.gender ? PersonFormat.Fields.gender(student.person.gender) : "Unknown"}
					</TruncatedText>
				</Row>
				<Row justify="spaced-start" align="center" verticalSpacing="0.25rem">
					<span className={styles["tooltip__label"]}>Nationality</span>
					<TruncatedText className={styles["tooltip__value"]}>
						{student.person.nationalities.length
							? PersonFormat.Edges.nationalities(student.person.nationalities)
							: "Unknown"}
					</TruncatedText>
				</Row>
				<Row justify="spaced-start" align="center" verticalSpacing="0.25rem">
					<span className={styles["tooltip__label"]}>Language</span>
					<TruncatedText className={styles["tooltip__value"]}>
						{student.person.languages.length ? PersonFormat.Edges.languages(student.person.languages) : "Unknown"}
					</TruncatedText>
				</Row>
			</Column>
		</Column>
	);
}

export type StudentTooltipContainerProps = Omit<TooltipContainerProps, "onOpenChange" | "contentSyncID"> & {
	studentID: Student["id"];
};

StudentTooltip.Container = function ({ studentID, children, ...props }: StudentTooltipContainerProps) {
	const [load, { loading, data }] = useStudentTooltipLazyQuery({
		variables: { id: studentID, date: toLocalDateString(new Date()) },
	});
	const { onOpenChange, contentSyncID, className: tooltipClassName } = useLazyTooltip(load, !!data);

	return (
		<Tooltip.Container onOpenChange={onOpenChange} contentSyncID={contentSyncID} {...props}>
			<StudentTooltip isLoading={loading} student={data?.student} className={tooltipClassName} />

			{children}
		</Tooltip.Container>
	);
};
